// Bacha Way Travel — pricing model (MAD)
// Unified with the fleet catalogue in src/lib/fleet.ts so the booking page,
// the public fleet page and quotes all share the same rates.

import { FLEET, type FleetId, type FleetVehicle } from "@/lib/fleet";

// IDs offered on the booking step (order matters — this is the rendered order).
export const BOOKABLE_VEHICLE_IDS: FleetId[] = [
  "economy",
  "premium-sedan",
  "family",
  "premium-van",
  "minibus",
  "luxury",
];

export type VehicleType = FleetId;

export interface VehicleInfo {
  id: VehicleType;
  name: string;
  image: string;
  capacity: number;
  luggage: number;
  description: string;
}

export const VEHICLES: Record<VehicleType, VehicleInfo> = FLEET.reduce(
  (acc, v: FleetVehicle) => {
    acc[v.id] = {
      id: v.id,
      name: v.name,
      image: v.image,
      capacity: v.pax,
      luggage: v.bags,
      description: v.description,
    };
    return acc;
  },
  {} as Record<VehicleType, VehicleInfo>,
);

export const MIN_FARE = 105;
const PRICE_MULTIPLIER = 0.7; // 30% discount applied to all fares

export interface PriceInput {
  vehicleType: VehicleType;
  distanceKm: number;
  tripType: "one-way" | "round-trip";
  timeHHmm?: string;
  memberDiscount?: boolean;
}

export interface PriceBreakdown {
  base: number;
  perKm: number;
  distanceKm: number;
  subtotal: number;
  roundTripMultiplier: number;
  nightSurcharge: number;
  memberDiscount: number;
  total: number;
}

export const MEMBER_DISCOUNT_RATE = 0.10;

export function estimatePrice(input: PriceInput): PriceBreakdown {
  const v = FLEET.find((f) => f.id === input.vehicleType) ?? FLEET[0];
  const km = Math.max(0, input.distanceKm || 0);
  let subtotal = (v.base + v.perKm * km) * PRICE_MULTIPLIER;
  if (subtotal < MIN_FARE) subtotal = MIN_FARE;

  const roundTripMultiplier = input.tripType === "round-trip" ? 2 : 1;

  let nightSurcharge = 0;
  if (input.timeHHmm) {
    const [hStr] = input.timeHHmm.split(":");
    const h = parseInt(hStr, 10);
    if (!isNaN(h) && (h >= 22 || h < 6)) {
      nightSurcharge = subtotal * roundTripMultiplier * 0.2;
    }
  }

  const gross = subtotal * roundTripMultiplier + nightSurcharge;
  const memberDiscount = input.memberDiscount ? gross * MEMBER_DISCOUNT_RATE : 0;
  const total = Math.round(gross - memberDiscount);

  return {
    base: v.base,
    perKm: v.perKm,
    distanceKm: km,
    subtotal: Math.round(subtotal),
    roundTripMultiplier,
    nightSurcharge: Math.round(nightSurcharge),
    memberDiscount: Math.round(memberDiscount),
    total,
  };
}

export function formatMAD(amount: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(amount) + " MAD";
}

